/**
 * Display-time dash normalization.
 *
 * Render-layer only — the DB content is untouched. Called from
 * render-question.tsx before block parsing so that em-dashes and
 * en-dashes in question / worked-solution / choice text render as
 * plain hyphens with sensible spacing.
 *
 * Rules:
 *   U+2014 (em-dash)                  →  " - "
 *   U+2013 (en-dash) between digits   →  "-"     (e.g. "2013–2014"
 *                                                 must stay "2013-2014",
 *                                                 no spaces gained)
 *   U+2013 (en-dash) elsewhere        →  " - "
 *
 * The dash replacement consumes one adjacent ASCII space on each
 * side if present, and emits exactly one space on each side, so no
 * double-spaces are ever introduced. Whitespace elsewhere in the
 * line (e.g. column-alignment padding inside an indented kv block
 * like "  Label:       $1,800") is intentionally left alone so the
 * downstream block parser still sees the original structure.
 *
 * Pure function: same input → same output, no side effects.
 */
export function normalizeDashes(text: string): string {
  if (!text) return text;
  return text
    // en-dash between digits → bare hyphen (year ranges, "5–10")
    .replace(/(\d)–(\d)/g, "$1-$2")
    // remaining en-dashes and em-dashes: optionally consume one ASCII
    // space (NOT a tab or newline) on each side, then emit " - ".
    // Result: exactly one space on each side, regardless of how many
    // (zero, one, or two) adjacent spaces were present at the dash
    // itself. Adjacent multi-space runs further out are preserved.
    .replace(/ ?[–—] ?/g, " - ");
}
